import React, { useEffect, useState, HTMLAttributes } from "react";
import { makeStyles, createStyles, Theme, useTheme } from "@material-ui/core/styles";
import INewsArticle from "apiConsumers/newsApiConsumer/models/INewsArticle";
import PageSection from "components/layout/PageSection";
import Typography from "@material-ui/core/Typography";
import { useParams } from "react-router-dom";
import { newsApi } from "apiConsumers";
import clsx from "clsx";
import NewsArticlePageContent from "./NewsArticlePageContent";
import imageSizes from "imageSizes";
import RelatedNews from "./RelatedNews";
import NewsArticleBackButton from "./NewsArticleBackButton";
import KenticoImg from "components/Img/KenticoImg";
import Spacer from "components/Spacer";
import { featureTileYellow } from "theme";
import jaggyTop from "assets/images/common/yellowJaggyEdges/yellow_jaggy_top_hires.png";
import jaggyBottom from "assets/images/common/yellowJaggyEdges/yellow_jaggy_bottom_hires.png";
import Rotate from "components/rotate/Rotate";
import { useMediaQuery } from "@material-ui/core";

const useStyles = makeStyles((theme: Theme) => {
    const getFeatureImageBorder = (size: number) => `${size}px solid ${featureTileYellow}`;

    return createStyles({
        root: {},
        childContainer: {
            display: "flex",
            flexDirection: "column",
        },
        articleContainer: {
            flex: "1 1 auto",
            display: "flex",
            flexDirection: "column",
        },
        articleHeaderImage: {},
        featureImageContainer: {
            position: "relative",
            overflow: "hidden",
            border: getFeatureImageBorder(8),
            [theme.breakpoints.down("xs")]: {
                border: getFeatureImageBorder(4),
            },
        },
        topBackbuttonContainer: {
            position: "absolute",
            left: -25,
            top: -25,
            [theme.breakpoints.down("xs")]: {
                left: -8,
                top: -17,
            },
        },
        articleContent: {
            margin: theme.spacing(0, 2),
            [theme.breakpoints.down("sm")]: {
                margin: theme.spacing(0, 1),
            },
            [theme.breakpoints.down("xs")]: {
                margin: theme.spacing(0),
            },
        },
    });
});

interface IProps extends HTMLAttributes<HTMLDivElement> {}

export default function NewsArticlePage(props: IProps) {
    const { className, ...rest } = props;
    const theme = useTheme();
    const xsScreen = useMediaQuery(theme.breakpoints.down("xs"));

    const classes = useStyles();
    const { articleId } = useParams();

    const [article, setArticle] = useState<INewsArticle>();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isError, setIsErrror] = useState<boolean>(false);

    useEffect(() => {
        window.scrollTo(0, 0);
        setIsLoading(true);
        if (typeof articleId === "undefined") {
            setArticle(undefined);
            setIsLoading(false);
        } else {
            newsApi
                .getArticle(articleId)
                .then((_article) => {
                    setArticle(_article);
                    setIsErrror(false);
                })
                .catch((err) => {
                    console.log(err);
                    setIsErrror(true);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [articleId]);

    return (
        <PageSection
            className={clsx(className, classes.root)}
            classes={{ childContainer: classes.childContainer }}
            {...rest}
        >
            <Spacer size={2} />

            <div className={classes.articleContainer}>
                {isError ? (
                    <Typography variant="body2">
                        There has been an error fetching the article. Please try again.
                    </Typography>
                ) : (
                    <>
                        <div className={classes.featureImageContainer}>
                            {typeof article?.featureImage?.url === "undefined" ? (
                                <KenticoImg
                                    src=""
                                    loading={isLoading}
                                    className={classes.articleHeaderImage}
                                    {...imageSizes.newsArticle}
                                />
                            ) : (
                                <KenticoImg
                                    src={article.featureImage.url}
                                    loading={isLoading}
                                    className={classes.articleHeaderImage}
                                    {...imageSizes.newsArticle}
                                />
                            )}
                            <img
                                src={jaggyBottom}
                                alt=""
                                style={{
                                    width: "100%",
                                    position: "absolute",
                                    top: "-0.5%",
                                }}
                            />
                            <img
                                src={jaggyTop}
                                alt=""
                                style={{
                                    width: "80%",
                                    position: "absolute",
                                    bottom: "-0.5%",
                                    right: 0,
                                }}
                            />
                        </div>

                        <div style={{ position: "relative" }}>
                            <NewsArticlePageContent
                                className={classes.articleContent}
                                article={article}
                                isLoading={isLoading}
                            />

                            <Rotate amount={-1} className={classes.topBackbuttonContainer}>
                                <NewsArticleBackButton size={xsScreen ? "small" : "medium"} />
                            </Rotate>
                        </div>
                    </>
                )}

                {typeof article?.url === "undefined" ? null : (
                    <>
                        <Spacer size={4} />
                        <RelatedNews articleUrl={article.url} />
                    </>
                )}
            </div>

            <Spacer size={4} />

            <div>
                <NewsArticleBackButton />
            </div>
        </PageSection>
    );
}
